// src/component/Pages/Discussion.jsx

import React, { useState, useEffect } from "react";
import { Container, Row, Col } from "react-bootstrap";
import { Typography, Box, Card } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { axiosInstance } from "../Api/instance";
import { PageHeader } from "../ui/PageHeaders";
import { PrimaryButton, SecondaryButton } from "../ui/Buttons";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { styled } from "@mui/material/styles";

const DiscussionPageContainer = styled(Box)(({ theme }) => ({
  minHeight: "85vh",
  padding: "32px 0",
  backgroundColor: theme.palette.background.default,
}));

const SupportCard = styled(Card)(({ theme }) => ({
  borderRadius: "24px",
  padding: "32px",
  backgroundColor: theme.palette.background.paper,
  border: `1px solid ${theme.palette.divider}`,
  boxShadow: "none",
  textAlign: "center",
  height: "100%",
  display: "flex",
  flexDirection: "column",
  justifyContent: "space-between",
  alignItems: "center",
  transition: "transform 0.2s ease, box-shadow 0.2s ease",
  "&:hover": {
    transform: "translateY(-4px)",
    boxShadow: "var(--color-shadow-hover)",
  },
}));

export default function Discussion() {
  const [data, setData] = useState([]);
  const email = localStorage.getItem("userMail");
  const navigate = useNavigate();

  useEffect(() => {
    fetchStaticInfo();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [email]);

  const fetchStaticInfo = async () => {
    try {
      const response = await axiosInstance.post(`get/U_ViewStaticInfo.php`, {
        userId: email,
      });
      if (response.status === 200) {
        if (response.data.message === "timeout") {
          navigate("/signin");
          return;
        }
        setData(Array.isArray(response.data) ? response.data : []);
      }
    } catch (error) {
      console.error("Error fetching static info:", error);
    }
  };

  return (
    <DiscussionPageContainer>
      <Container>
        {/* Modernized Page Header */}
        <PageHeader 
          title="Student Support & Forum" 
          subtitle="Get in touch with administrators and participate in clinical exam discussions."
          onBack={() => navigate("/home")}
        />

        {data.map((r) => (
          <Box key={r.id}>
            <Row className="g-4 justify-content-center align-items-stretch">
              
              {/* WhatsApp Support Channel */}
              <Col xs={12} md={6}>
                <SupportCard>
                  <Box style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                    <Box style={{ display: "flex", alignItems: "center", justifyContent: "center", width: "64px", height: "64px", borderRadius: "20px", backgroundColor: "rgba(34, 197, 94, 0.1)", color: "#22C55E", marginBottom: "20px" }}>
                      <WhatsAppIcon style={{ fontSize: 32 }} />
                    </Box>
                    <Typography variant="h3" style={{ fontWeight: 800, marginBottom: "8px" }}>
                      WhatsApp Discussion Forum
                    </Typography>
                    <Typography variant="body1" color="textSecondary" style={{ lineHeight: 1.6, marginBottom: "24px" }}>
                      Connect instantly with our administration and nursing student support community on WhatsApp.
                    </Typography>
                  </Box>
                  <PrimaryButton
                    href={r.whatsapplink}
                    target="_blank"
                    rel="noopener noreferrer"
                    endIcon={<ArrowForwardIcon />}
                    style={{
                      height: "46px",
                      borderRadius: "14px",
                      background: "linear-gradient(135deg, #22C55E 0%, #16A34A 100%)",
                      boxShadow: "0 4px 12px rgba(34, 197, 94, 0.2)",
                    }}
                  >
                    Join WhatsApp Group
                  </PrimaryButton>
                </SupportCard>
              </Col>

              {/* Gmail Support Channel */}
              <Col xs={12} md={6}>
                <SupportCard>
                  <Box style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                    <Box style={{ display: "flex", alignItems: "center", justifyContent: "center", width: "64px", height: "64px", borderRadius: "20px", backgroundColor: "rgba(30, 58, 138, 0.1)", color: "var(--color-primary)", marginBottom: "20px" }}>
                      <MailOutlineIcon style={{ fontSize: 32 }} />
                    </Box>
                    <Typography variant="h3" style={{ fontWeight: 800, marginBottom: "8px" }}>
                      Email Administration Support
                    </Typography>
                    <Typography variant="body1" color="textSecondary" style={{ lineHeight: 1.6, marginBottom: "24px" }}>
                      Submit your syllabus feedback, exam queries, or account problems directly to our support desk.
                    </Typography>
                  </Box>
                  <PrimaryButton
                    href={`mailto:${r.gmaillink}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    endIcon={<ArrowForwardIcon />}
                    style={{
                      height: "46px",
                      borderRadius: "14px",
                    }}
                  >
                    Send Support Email
                  </PrimaryButton>
                </SupportCard>
              </Col>

              {/* General support prompt */}
              <Col xs={12} style={{ textAlign: "center", marginTop: "40px" }}>
                <Typography variant="body1" color="textSecondary" style={{ marginBottom: "16px" }}>
                  Can't find what you are looking for? Learn more about our contact channels.
                </Typography>
                <SecondaryButton 
                  onClick={() => navigate("/contact")}
                  style={{ height: "42px", borderRadius: "12px", minWidth: "150px" }}
                >
                  Contact Help Desk
                </SecondaryButton>
              </Col>

            </Row>
          </Box>
        ))}
      </Container>
    </DiscussionPageContainer>
  );
}
